import React, {useMemo, useState} from 'react';
import {TextInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {IconSearch} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {SkillSignOffPendingResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  useCancelSkillSignOffMutation,
  useGetSkillSignOffOversightQuery,
} from 'src/redux/queries/skillSignOff';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const DAYS_WAITING_ALERT_THRESHOLD = 7;

export interface SignOffOversightTabProps {
  /** Seeds a client-side filter on the Course column — e.g. from the
   * closeout dialog's pending-demonstrations link, so the tab lands
   * pre-filtered to that class's course. The server `search` field only
   * matches trainee/skill name, not course, so this is a column filter on
   * the already-fetched page rather than a server param. */
  initialCourseName?: string;
}

/**
 * Admin oversight — every pending sign-off instance-wide, sorted by days
 * waiting by default. Row action opens the evaluation flow; no row-click
 * navigation per convention.
 */
export const SignOffOversightTab: React.FC<SignOffOversightTabProps> = ({
  initialCourseName,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [search, setSearch] = useState('');
  const [cancelTarget, setCancelTarget] = useState<
    SkillSignOffPendingResponse | undefined
  >();
  const {data, isLoading, isFetching, isError, error, refetch} =
    useGetSkillSignOffOversightQuery({
      start: 0,
      length: 1000,
      search: search.trim() || undefined,
      mineOnly: false,
    });
  const [cancelSignOff, {isLoading: isCancelling}] =
    useCancelSkillSignOffMutation();
  const rows = data ?? [];

  const handleCancel = async () => {
    if (!cancelTarget) {
      return;
    }
    try {
      await cancelSignOff(cancelTarget.id).unwrap();
      setAlert(
        `The sign-off request for ${cancelTarget.userName} was cancelled.`,
        'success'
      );
      setCancelTarget(undefined);
    } catch (cancelError) {
      setAlert(parseErrorMessage(cancelError), 'error');
    }
  };

  const columns = useMemo<MRT_ColumnDef<SkillSignOffPendingResponse>[]>(
    () => [
      {accessorKey: 'userName', header: 'Trainee'},
      {accessorKey: 'employeeId', header: 'Employee ID'},
      {accessorKey: 'skillName', header: 'Skill'},
      {
        accessorKey: 'courseName',
        header: 'Course',
        Cell: ({cell}) => cell.getValue<string | undefined>() ?? 'Standalone',
      },
      {
        accessorKey: 'requestedAt',
        header: 'Requested',
        Cell: ({cell}) =>
          parseUtcDate(cell.getValue<Date>()).toLocaleDateString(),
      },
      {
        accessorKey: 'daysWaiting',
        header: 'Days Waiting',
        Cell: ({cell}) => {
          const value = cell.getValue<number>();
          return (
            <Box c={value >= DAYS_WAITING_ALERT_THRESHOLD ? 'red' : undefined}>
              {Math.round(value)}
            </Box>
          );
        },
      },
      {
        id: 'suggestedEvaluator',
        header: 'Suggested Evaluator',
        accessorFn: row =>
          row.suggestedEvaluatorUserName ??
          row.suggestedEvaluatorUserGroupName ??
          '',
        Cell: ({row}) =>
          row.original.suggestedEvaluatorUserName ??
          row.original.suggestedEvaluatorUserGroupName ??
          '—',
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-sign-off-oversight',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'daysWaiting', desc: true}],
    defaultColumnFilters: initialCourseName
      ? [{id: 'courseName', value: initialCourseName}]
      : undefined,
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            tooltip="Evaluate"
            onClick={() =>
              navigate(
                PathUtility.TrainingSignOffEvaluate(String(row.original.id))
              )
            }
          />
          <TableRowActions.Menu>
            <TableRowActions.MenuItem
              color="red"
              onClick={() => setCancelTarget(row.original)}>
              Cancel request
            </TableRowActions.MenuItem>
          </TableRowActions.Menu>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <TextInput
              placeholder="Search trainee, employee ID or skill"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={event => setSearch(event.currentTarget.value)}
              w={280}
            />
          </TableToolbar.Filters>
        </TableToolbar>
      ),
    },
    totalCount: rows.length,
    isError,
    error: isError ? parseErrorMessage(error) : undefined,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {table}

      <ConfirmDialog
        opened={cancelTarget !== undefined}
        onClose={() => setCancelTarget(undefined)}
        onConfirm={handleCancel}
        title="Cancel sign-off request?"
        body={`The pending request for ${cancelTarget?.userName} on "${cancelTarget?.skillName}" will be removed.`}
        confirmationButtonColor="red"
        buttonTextYes="Cancel request"
        buttonTextNo="Keep it"
        confirmationButtonDisabled={isCancelling}
      />
    </>
  );
};
